import { useEffect, useState } from 'react'
import { fetchUsers, saveUser, deleteUser, type ManagedUser } from '../api/users'
import { InfoTip } from './InfoTip'
import { SearchableSelect } from './SearchableSelect'

const API_BASE = import.meta.env.VITE_API_URL ?? ''

// MP-OS-93 — the admin's self-service page: create/edit/remove logins and assign each
// supplier login the supplier it may see, so the customer onboards their own suppliers
// without a CLI. Admin-only; the server re-checks the role on every call.
interface SupplierOption {
  value: string
  label: string
}

const emptyForm = { username: '', name: '', password: '', role: 'supplier' as 'admin' | 'supplier', manufacturer: '', notify: true }

export function AdminUsers() {
  const [users, setUsers] = useState<ManagedUser[] | null>(null)
  const [suppliers, setSuppliers] = useState<SupplierOption[]>([])
  const [form, setForm] = useState({ ...emptyForm })
  const [editing, setEditing] = useState(false) // editing an existing user → password optional
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [usersError, setUsersError] = useState<string | null>(null)
  const [suppliersError, setSuppliersError] = useState<string | null>(null)
  const [loadingSuppliers, setLoadingSuppliers] = useState(true)
  // The edited login's stored scope, as saved (MPD-100 #2). Kept verbatim unless the admin
  // picks another supplier, so a name, email or role edit never rewrites the scope.
  const [storedScope, setStoredScope] = useState<SupplierOption | null>(null)

  const reload = async () => {
    setUsersError(null)
    try {
      setUsers(await fetchUsers())
    } catch (e) {
      setUsersError(e instanceof Error ? e.message : 'Could not load users.')
    }
  }

  const reloadSuppliers = async () => {
    setLoadingSuppliers(true)
    setSuppliersError(null)
    try {
      const response = await fetch(`${API_BASE}/api/manufacturers`, { credentials: 'include' })
      if (!response.ok) throw new Error(`Could not load suppliers (HTTP ${response.status}).`)
      const data = await response.json()
      if (!Array.isArray(data.manufacturers)) throw new Error('Could not load suppliers: invalid response.')
      setSuppliers(data.manufacturers)
    } catch (e) {
      setSuppliersError(e instanceof Error ? e.message : 'Could not load suppliers.')
    } finally {
      setLoadingSuppliers(false)
    }
  }

  useEffect(() => {
    reload()
    reloadSuppliers()
  }, [])

  const resetForm = () => {
    setForm({ ...emptyForm })
    setEditing(false)
    setStoredScope(null)
    setError(null)
  }

  const startEdit = (u: ManagedUser) => {
    // The form keeps the stored value as is. It used to swap in the picker entry with the same
    // label ("71" → "71,332"), and a picker value can scope more than the stored one, so a
    // name-only edit could widen a login's scope (MPD-100 #2). A stored value that matches no
    // picker entry is offered as its own labelled entry below.
    const stored = u.manufacturer ?? ''
    setStoredScope(stored ? { value: stored, label: `${u.manufacturer_label ?? stored} (saved scope: ${stored})` } : null)
    setForm({ username: u.username, name: u.name, password: '', role: u.role, manufacturer: stored, notify: true })
    setEditing(true)
    setError(null)
    setNotice(null)
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (busy) return
    setError(null)
    setNotice(null)
    if ((!editing && !form.password) || (form.password && form.password.length < 8)) {
      setError('The password must be at least 8 characters.')
      return
    }
    // The picker is a button, not a native required <select>: check the required supplier here.
    if (form.role === 'supplier' && !form.manufacturer) {
      setError('Select a supplier for this login.')
      return
    }
    setBusy(true)
    try {
      const saved = await saveUser({
        username: form.username.trim(),
        password: form.password ? form.password : undefined,
        name: form.name.trim() || undefined,
        email: form.username.includes('@') ? form.username.trim() : undefined,
        role: form.role,
        manufacturer: form.role === 'supplier' ? form.manufacturer : undefined,
        notify: form.password ? form.notify : undefined,
      })
      // Say whether the person was actually TOLD. "Saved" on its own once left an admin
      // believing a reset had reached the user when nothing had been sent (MP-OS-139).
      const who = form.username.trim()
      setNotice(
        !form.password
          ? `Saved ${who}.`
          : saved.invite_sent
            ? `Saved ${who} — login details emailed.`
            : saved.invite_requested
              ? `Saved ${who}, but the email could not be sent. Give them the password yourself.`
              : `Saved ${who}. No email sent — give them the password yourself.`,
      )
      resetForm()
      await reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.')
    } finally {
      setBusy(false)
    }
  }

  // The stored scope value is a code / id-list ("JTI", "320,129"); show the human name so
  // the table matches the dropdown (Olof: "finns både Japan Tobacco International och JTI").
  const supplierLabel = (u: ManagedUser) =>
    u.manufacturer ? suppliers.find((s) => s.value === u.manufacturer)?.label ?? u.manufacturer_label ?? u.manufacturer : '—'

  const remove = async (u: ManagedUser) => {
    if (!confirm(`Remove the login for ${u.name} (${u.username})?`)) return
    setError(null)
    try {
      await deleteUser(u.username)
      setNotice(`Removed ${u.username}.`)
      await reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove.')
    }
  }

  return (
    <div className="section-pad">
      <h1 className="section-h1">Users & logins</h1>
      {/* MPD-64: the roles are explained in the ⓘ; the password warning sits by the field. */}
      <p style={{ color: 'var(--grey-text, #667)', marginTop: -8, marginBottom: 20, maxWidth: 640 }}>
        Logins and their suppliers
        <InfoTip label="About users and logins" size="small">
          Create logins and assign each supplier login the supplier it represents. A supplier sees only its
          own data; an admin sees everything.
        </InfoTip>
      </p>

      {error && <div role="alert" style={{ color: '#b00', marginBottom: 12 }}>{error}</div>}
      {usersError && <div role="alert" style={{ color: '#b00', marginBottom: 12 }}>
        {usersError} <button type="button" onClick={reload} style={ghostBtn}>Retry users</button>
      </div>}
      {suppliersError && <div role="alert" style={{ color: '#b00', marginBottom: 12 }}>
        {suppliersError} <button type="button" onClick={reloadSuppliers} style={ghostBtn}>Retry suppliers</button>
      </div>}
      {notice && <div role="status" style={{ color: 'var(--brand-dark, #060)', marginBottom: 12 }}>{notice}</div>}

      <form onSubmit={submit} style={{ display: 'grid', gap: 10, maxWidth: 520, marginBottom: 28 }}>
        <label style={{ display: 'grid', gap: 4 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>Username / email</span>
          <input
            required
            value={form.username}
            disabled={editing}
            onChange={(e) => setForm({ ...form, username: e.target.value })}
            placeholder="e.g. linus.karlsson@leancom.se"
            style={inputStyle}
          />
        </label>
        <label style={{ display: 'grid', gap: 4 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>Name</span>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} style={inputStyle} />
        </label>
        <label style={{ display: 'grid', gap: 4 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>
            Password {editing && <em style={{ fontWeight: 400 }}>(leave blank to keep)</em>}
          </span>
          <input
            type="password"
            required={!editing}
            minLength={8}
            autoComplete="new-password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            placeholder={editing ? '••••••••' : 'at least 8 characters'}
            style={inputStyle}
          />
          <span style={{ fontSize: 12, color: 'var(--grey-text, #667)' }}>The password is never shown again after it is saved.</span>
        </label>
        {form.password && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={form.notify}
              onChange={(e) => setForm({ ...form, notify: e.target.checked })}
            />
            <span>Email the login details to this address</span>
          </label>
        )}
        <label style={{ display: 'grid', gap: 4 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>Role</span>
          <select
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value as 'admin' | 'supplier' })}
            style={inputStyle}
          >
            <option value="supplier">Supplier (sees only its own data)</option>
            <option value="admin">Admin (sees everything)</option>
          </select>
        </label>
        {form.role === 'supplier' && (
          <div style={{ display: 'grid', gap: 4 }}>
            {/* A <div>, not a <label>: a label forwards clicks on the picker's options to its button. */}
            <span style={{ fontSize: 13, fontWeight: 600 }} aria-hidden>Supplier</span>
            {/* MP-OS-189: every supplier in the data (~56) — the shared searchable picker. */}
            <SearchableSelect
              label="Supplier"
              searchLabel="Search suppliers"
              showAllLabel="Show all suppliers"
              required
              disabled={!!suppliersError}
              loading={loadingSuppliers}
              loadingText="Loading suppliers…"
              value={form.manufacturer}
              onChange={(manufacturer) => setForm({ ...form, manufacturer })}
              triggerClassName="ss-trigger"
              style={{ ...inputStyle, background: 'var(--surface, #fff)', color: 'inherit', cursor: 'pointer', width: '100%' }}
              className="searchable-select searchable-select--block"
              options={[
                { value: '', label: 'Select supplier…' },
                ...(storedScope && !suppliers.some((s) => s.value === storedScope.value) ? [storedScope] : []),
                ...suppliers,
              ]}
            />
          </div>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="submit" disabled={busy || (form.role === 'supplier' && (loadingSuppliers || !!suppliersError))} style={primaryBtn}>
            {editing ? 'Save changes' : 'Create login'}
          </button>
          {editing && (
            <button type="button" onClick={resetForm} disabled={busy} style={ghostBtn}>
              Cancel
            </button>
          )}
        </div>
      </form>

      <table className="sku-table" style={{ width: '100%' }}>
        <thead>
          <tr>
            <th>Name</th>
            <th>Username</th>
            <th>Role</th>
            <th>Supplier</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {users?.map((u) => (
            <tr key={u.username}>
              <td>{u.name}</td>
              <td>{u.username}</td>
              <td>{u.role === 'admin' ? 'Admin' : 'Supplier'}</td>
              <td>{supplierLabel(u)}</td>
              <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                <button onClick={() => startEdit(u)} style={ghostBtn}>Edit</button>{' '}
                <button onClick={() => remove(u)} style={{ ...ghostBtn, color: '#b00' }}>Remove</button>
              </td>
            </tr>
          ))}
          {users && users.length === 0 && (
            <tr><td colSpan={5} style={{ color: 'var(--grey-text,#667)' }}>No users yet.</td></tr>
          )}
        </tbody>
      </table>
      {users === null && !usersError && <div style={{ color: 'var(--grey-text,#667)', marginTop: 12 }}>Loading…</div>}
    </div>
  )
}

const inputStyle: React.CSSProperties = {
  padding: '8px 10px',
  borderRadius: 8,
  border: '1px solid var(--border, #ddd)',
  fontSize: 14,
}
const primaryBtn: React.CSSProperties = {
  padding: '8px 16px',
  borderRadius: 8,
  border: 'none',
  background: 'var(--brand-dark, #0a7)',
  color: '#fff',
  fontWeight: 600,
  cursor: 'pointer',
}
const ghostBtn: React.CSSProperties = {
  padding: '6px 12px',
  borderRadius: 8,
  border: '1px solid var(--border, #ddd)',
  background: 'transparent',
  cursor: 'pointer',
  fontSize: 13,
}
